import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import { observer } from 'mobx-react-lite';
import * as styles from '@components/auth/ForgotPasswordModal.module.css';

interface ForgotPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = observer(
    ({ isOpen, onClose }) => {
        const { t } = useTranslation();
        const authStore = useAuthStore();
        const toast = useToastContext();
        const [email, setEmail] = useState('');
        const [loading, setLoading] = useState(false);
        const [success, setSuccess] = useState(false);

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();

            if (!email) {
                toast.warning(t('auth.forgotPassword.enterEmail'));
                return;
            }

            setLoading(true);
            try {
                await authStore.requestReset(email);
                setSuccess(true);
                toast.success(t('auth.forgotPassword.emailSent'));
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.message || t('auth.forgotPassword.requestFailed');
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        const handleClose = () => {
            setEmail('');
            setSuccess(false);
            onClose();
        };

        useEffect(() => {
            if (isOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
            return () => {
                document.body.style.overflow = '';
            };
        }, [isOpen]);

        useEffect(() => {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape' && isOpen) {
                    handleClose();
                }
            };
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }, [isOpen]);

        if (!isOpen) return null;

        return (
            <div className={styles.overlay} onClick={handleClose}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.title}>{t('auth.forgotPassword.title')}</h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className={styles.closeButton}
                            aria-label={t('auth.forgotPassword.closeAria')}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        {success ? (
                            <div className={styles.successContent}>
                                <div className={styles.successIcon}>✓</div>
                                <h3 className={styles.successTitle}>
                                    {t('auth.forgotPassword.successTitle')}
                                </h3>
                                <p className={styles.successText}>
                                    {t('auth.forgotPassword.successText', { email })}
                                </p>
                                <p className={styles.successHint}>
                                    {t('auth.forgotPassword.spamHint')}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className={styles.closeSuccessButton}
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className={styles.description}>
                                    {t('auth.forgotPassword.description')}
                                </p>
                                <form onSubmit={handleSubmit} className={styles.form}>
                                    <div className={styles.field}>
                                        <label htmlFor="resetEmail" className={styles.label}>
                                            {t('auth.forgotPassword.emailLabel')}
                                        </label>
                                        <input
                                            id="resetEmail"
                                            type="email"
                                            placeholder={t('auth.forgotPassword.emailPlaceholder')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className={styles.input}
                                            disabled={loading}
                                            autoFocus
                                        />
                                    </div>

                                    <div className={styles.actions}>
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className={styles.cancelButton}
                                            disabled={loading}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            className={styles.submitButton}
                                            disabled={loading}
                                        >
                                            {loading
                                                ? t('auth.forgotPassword.submitting')
                                                : t('auth.forgotPassword.submit')}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
